/**
 * PATH: packages/ui/src/kanban/components/board-view/cardFacePrefs.ts
 * FILE_ID: UI-KANBAN-CARD-FACE-PREFS-001
 * PURPOSE: Per-operator card-face layout — tile blocks, field chrome
 *          (due), and Also-show placement. Local bag plus UserViewPreference
 *          scope `kanban:card-face`. Optional per-board overrides.
 * WHY: Operator 2026-08-31 block layout, plus Inbox hide-due / show
 *      allocator (change #755). Do not mint /api/kanban/prefs/.
 * FLOW:
 *   userId → bag.cardFace + view-pref kanban:card-face → resolve(board)
 * DEPENDENCIES: React useSyncExternalStore; dbViewPreference.
 * RELATED: ./dockPrefs.ts (same bag). Title cannot be hidden.
 * NOTES FOR FUTURE AI: Unset fields mean Inbox due off + allocator rail;
 *   other boards due on + allocator menu. Explicit prefs win.
 */

import { useCallback, useSyncExternalStore } from 'react'

import { scheduleServerSave } from '@quotech/tenant-shared/preferences/dbViewPreference'

import { useOptionalKanbanSettingsPreferences } from '../../preferences/KanbanSettingsPreferenceContext'
import {
  coerceRecipeChrome,
  recipeChromeIsEmpty,
  resolveRecipeChrome,
  sparseRecipeChrome,
  type CardFaceCompleteMode,
  type CardFaceComposeSeat,
  type CardFaceExtraMap,
  type CardFaceFieldId,
  type CardFaceFieldMap,
  type CardFaceMenuId,
  type CardFaceMenuMap,
  type CardFaceMenuPlace,
  type CardFaceOnOff,
  type CardFacePlaceMap,
  type CardFacePlaceMode,
  type CardFaceRailId,
  type CardFaceRailMode,
  type CardFaceStripId,
  type CardFaceStripMap,
  type CardFaceTileBody,
  type CardFaceTileOpen,
  type CardFaceExtraId,
} from './cardFaceRecipe'

export const CARD_FACE_BLOCK_IDS = [
  'labels',
  'title',
  'actionLine',
  'stateStrip',
  'emailMeta',
  'meta',
  'quoteFacts',
  'actionBar',
  'capture',
] as const

export type CardFaceBlockId = (typeof CARD_FACE_BLOCK_IDS)[number]

export const REQUIRED_CARD_FACE_BLOCKS: readonly CardFaceBlockId[] = ['title']

/** Blocks that appear on the compact tile the operator screenshotted. */
export const COMPACT_CARD_FACE_BLOCKS: readonly CardFaceBlockId[] = [
  'labels',
  'title',
  'meta',
  'quoteFacts',
  'actionBar',
  'capture',
]

export type {
  CardFaceCompleteMode,
  CardFaceComposeSeat,
  CardFaceExtraMap,
  CardFaceFieldId,
  CardFaceFieldMap,
  CardFaceMenuMap,
  CardFacePlaceMap,
  CardFacePlaceMode,
  CardFaceRailMode,
  CardFaceStripMap,
  CardFaceTileBody,
  CardFaceTileOpen,
} from './cardFaceRecipe'
export type CardFaceAllocatorMode = import('./cardFaceRecipe').CardFacePlaceMode
export type CardFaceFieldPrefs = CardFaceFieldMap
export type CardFacePlacePrefs = CardFacePlaceMap

export interface CardFaceBoardOverride {
  order?: CardFaceBlockId[]
  hidden?: CardFaceBlockId[]
  fields?: CardFaceFieldMap
  place?: CardFacePlaceMap
  strip?: CardFaceStripMap
  menu?: CardFaceMenuMap
  extras?: CardFaceExtraMap
  compose?: CardFaceComposeSeat
  railMode?: CardFaceRailMode
  tileBody?: CardFaceTileBody
  tileOpen?: CardFaceTileOpen
  complete?: CardFaceCompleteMode
}

export interface KanbanCardFacePrefs {
  order: CardFaceBlockId[]
  hidden: CardFaceBlockId[]
  fields: CardFaceFieldMap
  place: CardFacePlaceMap
  strip: CardFaceStripMap
  menu: CardFaceMenuMap
  extras: CardFaceExtraMap
  compose?: CardFaceComposeSeat
  railMode?: CardFaceRailMode
  tileBody?: CardFaceTileBody
  tileOpen?: CardFaceTileOpen
  complete?: CardFaceCompleteMode
  boardOverrides: Record<string, CardFaceBoardOverride>
}

export interface ResolvedCardFacePrefs extends KanbanCardFacePrefs {
  fields: Record<CardFaceFieldId, boolean>
  place: Record<CardFaceRailId, CardFacePlaceMode>
  strip: Record<CardFaceStripId, CardFaceOnOff>
  menu: Record<CardFaceMenuId, CardFaceMenuPlace>
  extras: Record<CardFaceExtraId, CardFaceOnOff>
  compose: CardFaceComposeSeat
  railMode: CardFaceRailMode
  tileBody: CardFaceTileBody
  tileOpen: CardFaceTileOpen
  complete: CardFaceCompleteMode
}

export const DEFAULT_CARD_FACE_PREFS: KanbanCardFacePrefs = {
  order: [...CARD_FACE_BLOCK_IDS],
  hidden: [],
  fields: {},
  place: {},
  strip: {},
  menu: {},
  extras: {},
  boardOverrides: {},
}

export const CARD_FACE_DB_SCOPE = 'kanban:card-face'

const CHANGE_EVENT = 'kanban:card-face-prefs-changed'

function userSegment(userId: string | number | null | undefined): string {
  return userId === null || userId === undefined || userId === ''
    ? 'anon'
    : String(userId)
}

function storageKey(userId: string | number | null | undefined): string {
  return `kanban:prefs:${userSegment(userId)}`
}

function readPrefsBag(
  userId: string | number | null | undefined,
): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

function isBlockId(value: unknown): value is CardFaceBlockId {
  return (CARD_FACE_BLOCK_IDS as readonly string[]).includes(String(value))
}

function recipeFrom(raw: unknown): ReturnType<typeof coerceRecipeChrome> {
  if (!raw || typeof raw !== 'object') return coerceRecipeChrome({})
  return coerceRecipeChrome(raw as Record<string, unknown>)
}

function coerceHiddenList(raw: unknown): CardFaceBlockId[] {
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (id): id is CardFaceBlockId =>
      isBlockId(id) && !REQUIRED_CARD_FACE_BLOCKS.includes(id),
  )
}

function coerceOrderList(raw: unknown): CardFaceBlockId[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const seen = new Set<CardFaceBlockId>()
  const order: CardFaceBlockId[] = []
  for (const item of raw) {
    if (isBlockId(item) && !seen.has(item)) {
      order.push(item)
      seen.add(item)
    }
  }
  if (order.length === 0) return undefined
  for (const id of CARD_FACE_BLOCK_IDS) {
    if (!seen.has(id)) order.push(id)
  }
  return order
}

function coerceBoardOverrides(raw: unknown): Record<string, CardFaceBoardOverride> {
  if (!raw || typeof raw !== 'object') return {}
  const next: Record<string, CardFaceBoardOverride> = {}
  for (const [boardId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!boardId || !value || typeof value !== 'object') continue
    const row = value as Record<string, unknown>
    const chrome = recipeFrom(row)
    next[boardId] = {
      order: coerceOrderList(row.order),
      hidden: coerceHiddenList(row.hidden),
      ...chrome,
    }
  }
  return next
}

export function coerceCardFacePrefs(raw: unknown): KanbanCardFacePrefs {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const seen = new Set<CardFaceBlockId>()
  const order: CardFaceBlockId[] = []
  if (Array.isArray(o.order)) {
    for (const item of o.order) {
      if (isBlockId(item) && !seen.has(item)) {
        order.push(item)
        seen.add(item)
      }
    }
  }
  for (const id of CARD_FACE_BLOCK_IDS) {
    if (!seen.has(id)) order.push(id)
  }
  const chrome = recipeFrom(o)
  return {
    order,
    hidden: coerceHiddenList(o.hidden),
    ...chrome,
    boardOverrides: coerceBoardOverrides(o.boardOverrides),
  }
}

/** Resolve all-boards prefs plus optional this-board override.
 *  Inbox with no explicit due/allocator uses due off and allocator rail. */
export function resolveCardFacePrefs(
  prefs: KanbanCardFacePrefs,
  ctx?: { boardId?: string; boardKind?: string | null },
): ResolvedCardFacePrefs {
  const override = ctx?.boardId ? prefs.boardOverrides[ctx.boardId] : undefined
  const isInbox = ctx?.boardKind === 'INBOX'
  const recipe = resolveRecipeChrome(prefs, override, isInbox)
  return {
    ...prefs,
    ...recipe,
    order: override?.order ?? prefs.order,
    hidden: override?.hidden ?? prefs.hidden,
    boardOverrides: prefs.boardOverrides,
  }
}

export function patchCardFaceAllBoards(
  prefs: KanbanCardFacePrefs,
  patch: Partial<Omit<KanbanCardFacePrefs, 'boardOverrides'>>,
): KanbanCardFacePrefs {
  return {
    ...prefs,
    ...patch,
    order: patch.order ?? prefs.order,
    hidden: patch.hidden ?? prefs.hidden,
    fields: { ...prefs.fields, ...patch.fields },
    place: { ...prefs.place, ...patch.place },
    strip: { ...prefs.strip, ...patch.strip },
    menu: { ...prefs.menu, ...patch.menu },
    extras: { ...prefs.extras, ...patch.extras },
  }
}

function sameIdList(
  left: readonly string[] | undefined,
  right: readonly string[],
): boolean {
  return !!left && left.length === right.length && left.every((id, i) => id === right[i])
}

export function patchCardFaceBoard(
  prefs: KanbanCardFacePrefs,
  boardId: string,
  patch: CardFaceBoardOverride,
): KanbanCardFacePrefs {
  const prev = prefs.boardOverrides[boardId] ?? {}
  const merged: CardFaceBoardOverride = {
    ...prev,
    ...patch,
    order: patch.order ?? prev.order,
    hidden: patch.hidden ?? prev.hidden,
    fields: { ...prev.fields, ...patch.fields },
    place: { ...prev.place, ...patch.place },
    strip: { ...prev.strip, ...patch.strip },
    menu: { ...prev.menu, ...patch.menu },
    extras: { ...prev.extras, ...patch.extras },
  }
  const chrome = sparseRecipeChrome(prefs, {
    fields: merged.fields ?? {},
    place: merged.place ?? {},
    strip: merged.strip ?? {},
    menu: merged.menu ?? {},
    extras: merged.extras ?? {},
    compose: merged.compose,
    railMode: merged.railMode,
    tileBody: merged.tileBody,
    tileOpen: merged.tileOpen,
    complete: merged.complete,
  })
  const order = sameIdList(merged.order, prefs.order) ? undefined : merged.order
  const hidden = sameIdList(merged.hidden, prefs.hidden) ? undefined : merged.hidden
  const rest = { ...prefs.boardOverrides }
  delete rest[boardId]
  if (!order && !hidden && recipeChromeIsEmpty(chrome)) {
    return { ...prefs, boardOverrides: rest }
  }
  return {
    ...prefs,
    boardOverrides: {
      ...rest,
      [boardId]: {
        ...(Object.keys(chrome.fields).length ? { fields: chrome.fields } : {}),
        ...(Object.keys(chrome.place).length ? { place: chrome.place } : {}),
        ...(Object.keys(chrome.strip).length ? { strip: chrome.strip } : {}),
        ...(Object.keys(chrome.menu).length ? { menu: chrome.menu } : {}),
        ...(Object.keys(chrome.extras).length ? { extras: chrome.extras } : {}),
        ...(chrome.compose ? { compose: chrome.compose } : {}),
        ...(chrome.railMode ? { railMode: chrome.railMode } : {}),
        ...(chrome.tileBody ? { tileBody: chrome.tileBody } : {}),
        ...(chrome.tileOpen ? { tileOpen: chrome.tileOpen } : {}),
        ...(chrome.complete ? { complete: chrome.complete } : {}),
        ...(order ? { order } : {}),
        ...(hidden ? { hidden } : {}),
      },
    },
  }
}

export function readKanbanCardFacePrefs(
  userId: string | number | null | undefined,
): KanbanCardFacePrefs {
  return coerceCardFacePrefs(readPrefsBag(userId).cardFace)
}

export function writeKanbanCardFacePrefs(
  userId: string | number | null | undefined,
  prefs: KanbanCardFacePrefs,
): void {
  const coerced = coerceCardFacePrefs(prefs)
  if (typeof window === 'undefined') return
  try {
    const bag = readPrefsBag(userId)
    bag.cardFace = coerced
    window.localStorage.setItem(storageKey(userId), JSON.stringify(bag))
  } catch {
    /* quota / disabled storage — the event still updates this session */
  }
  scheduleServerSave(CARD_FACE_DB_SCOPE, coerced)
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

export function isCardFaceBlockVisible(
  prefs: KanbanCardFacePrefs,
  id: CardFaceBlockId,
): boolean {
  if (REQUIRED_CARD_FACE_BLOCKS.includes(id)) return true
  return !prefs.hidden.includes(id)
}

export function orderedVisibleCardFaceBlocks(
  prefs: KanbanCardFacePrefs,
): CardFaceBlockId[] {
  return prefs.order.filter((id) => isCardFaceBlockVisible(prefs, id))
}

export function toggleCardFaceBlock(
  prefs: KanbanCardFacePrefs,
  id: CardFaceBlockId,
): KanbanCardFacePrefs {
  if (REQUIRED_CARD_FACE_BLOCKS.includes(id)) return prefs
  const hidden = prefs.hidden.includes(id)
    ? prefs.hidden.filter((h) => h !== id)
    : [...prefs.hidden, id]
  return { ...prefs, hidden }
}

export function moveCardFaceBlock(
  prefs: KanbanCardFacePrefs,
  id: CardFaceBlockId,
  direction: -1 | 1,
): KanbanCardFacePrefs {
  const index = prefs.order.indexOf(id)
  if (index < 0) return prefs
  const next = index + direction
  if (next < 0 || next >= prefs.order.length) return prefs
  const order = [...prefs.order]
  const [row] = order.splice(index, 1)
  order.splice(next, 0, row)
  return { ...prefs, order }
}

let snapshotCache: { key: string; raw: string; value: KanbanCardFacePrefs } | null =
  null

function getCachedPrefs(
  userId: string | number | null | undefined,
): KanbanCardFacePrefs {
  const key = storageKey(userId)
  let raw = ''
  if (typeof window !== 'undefined') {
    try {
      raw = window.localStorage.getItem(key) ?? ''
    } catch {
      raw = ''
    }
  }
  if (snapshotCache && snapshotCache.key === key && snapshotCache.raw === raw) {
    return snapshotCache.value
  }
  const value = readKanbanCardFacePrefs(userId)
  snapshotCache = { key, raw, value }
  return value
}

export function useKanbanCardFacePrefs(
  userId: string | number | null | undefined,
): [KanbanCardFacePrefs, (next: KanbanCardFacePrefs) => void] {
  const preferenceRuntime = useOptionalKanbanSettingsPreferences()
  const providerOwnsSubscription = preferenceRuntime !== null
  const subscribe = useCallback((onChange: () => void) => {
    if (providerOwnsSubscription) return () => {}
    if (typeof window === 'undefined') return () => {}
    window.addEventListener(CHANGE_EVENT, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [providerOwnsSubscription])
  const getSnapshot = useCallback(
    () => providerOwnsSubscription ? DEFAULT_CARD_FACE_PREFS : getCachedPrefs(userId),
    [providerOwnsSubscription, userId],
  )
  const prefs = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => DEFAULT_CARD_FACE_PREFS,
  )
  const setPrefs = useCallback(
    (next: KanbanCardFacePrefs) => writeKanbanCardFacePrefs(userId, next),
    [userId],
  )
  return preferenceRuntime
    ? [preferenceRuntime.cardFace.value, preferenceRuntime.cardFace.setValue]
    : [prefs, setPrefs]
}
